/* eslint-disable */
<template>
  <div class="avail-calendar">

    <!-- Selected slot banner -->
    <transition name="fade">
      <div v-if="selectedSlotLabel" class="slot-banner">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <div><strong>Confirmed:</strong> {{ selectedSlotLabel }}</div>
        <button type="button" class="change-btn" @click="clearSlot">✕ Change</button>
      </div>
    </transition>

    <!-- Calendar card -->
    <div class="cal-card">

      <!-- Month nav + legend -->
      <div class="cal-nav">
        <button type="button" class="nav-btn" @click="changeMonth(-1)">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <span class="month-label">{{ monthLabel }}</span>
        <button type="button" class="nav-btn" @click="changeMonth(1)">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        <div class="cal-legend">
          <span class="leg"><span class="leg-dot leg-avail"></span>Available</span>
          <span class="leg"><span class="leg-dot leg-partial"></span>Partially booked</span>
          <span class="leg"><span class="leg-dot leg-full"></span>Fully booked</span>
        </div>
      </div>

      <!-- Status key -->
      <div class="status-key">
        <span class="sk-item">
          <span class="status-pill sp-waiting">waiting</span>
          <span class="sk-arrow">+</span>
          <span class="status-pill sp-processing">processing</span>
          <span class="sk-arrow">+</span>
          <span class="status-pill sp-payment">payment</span>
          <span class="sk-arrow">→</span>
          <span class="leg-dot leg-partial" style="flex-shrink:0"></span>
          <span class="sk-label">Partially booked</span>
        </span>
        <span class="sk-sep">|</span>
        <span class="sk-item">
          <span class="status-pill sp-paid">paid</span>
          <span class="sk-arrow">→</span>
          <span class="leg-dot leg-full" style="flex-shrink:0"></span>
          <span class="sk-label">Fully booked</span>
        </span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="cal-loading">
        <svg class="spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        Loading availability…
      </div>

      <!-- Calendar grid -->
      <div v-else class="cal-grid">
        <div class="cal-dh" v-for="d in ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']" :key="d">{{ d }}</div>
        <div class="cal-empty" v-for="n in firstOffset" :key="'e'+n"></div>
        <div
          v-for="d in daysInMonth"
          :key="d"
          class="cal-day"
          :class="{
            'cal-past':     isPast(d),
            'cal-today':    isToday(d),
            'cal-selected': selectedDay === d,
            'cal-fullday':  !isPast(d) && isDayFullyBooked(d),
          }"
          @click="selectDay(d)"
        >
          <span class="cal-day-num">{{ d }}</span>
          <template v-if="!isPast(d)">
            <div v-if="isDayFullyBooked(d)" class="fullday-bar">FULL DAY</div>
            <div v-else class="cal-dots">
              <span
                v-for="(s, i) in slotsForView"
                :key="i"
                class="cal-dot"
                :class="'dot-' + getSlotStatus(d, i)"
              ></span>
            </div>
          </template>
        </div>
      </div>

      <!-- Slot panel -->
      <transition name="fade">
        <div v-if="selectedDay" class="slot-panel">

          <div class="slot-panel-hd">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <strong>{{ selectedDateLabel }}</strong>
          </div>

          <!-- Full day booked notice -->
          <div v-if="isDayFullyBooked(selectedDay)" class="fd-unavail">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
            </svg>
            This date is fully confirmed — no slots available.
          </div>

          <div v-else>
            <!-- Booking type tabs -->
            <div v-if="supportsFullDay" class="type-tabs">
              <button
                type="button"
                class="type-tab"
                :class="{ active: bookingMode === 'slot' }"
                @click="bookingMode = 'slot'; clearSlot()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Time slot
              </button>
              <button
                type="button"
                class="type-tab"
                :class="{ active: bookingMode === 'fullday' }"
                @click="bookingMode = 'fullday'; clearSlot()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
                </svg>
                Full day
                <span class="tab-sub">{{ fullDaySlot.label }}</span>
              </button>
            </div>

            <!-- Full Day mode -->
            <div v-if="bookingMode === 'fullday'" class="fullday-section">
              <div v-if="canBookFullDay(selectedDay)" class="fd-available">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <strong>Full day available</strong>
                  <span class="fd-sub">{{ fullDaySlot.label }} · blocks all time slots</span>
                </div>
                <button
                  type="button"
                  class="pick-btn"
                  :class="{ picked: isFullDaySelected }"
                  @click="pickFullDay"
                >
                  {{ isFullDaySelected ? '✓ Selected' : 'Select' }}
                </button>
              </div>
              <div v-else class="fd-unavail">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                Full day not available — some slots are already held or booked.
                <br>Switch to "Time slot" to book individual sessions.
              </div>
            </div>

            <!-- Time Slot mode -->
            <div v-else class="slot-list">
              <div
                v-for="(slot, i) in slotsForView"
                :key="i"
                class="slot-row"
                :class="{
                  'slot-busy':   getSlotStatus(selectedDay, i) === 'busy',
                  'slot-picked': selectedSlot && selectedSlot.start === slot.start,
                }"
                @click="getSlotStatus(selectedDay, i) !== 'busy' && pickSlot(slot, i)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span class="slot-time">{{ slot.label }}</span>

                <span class="slot-badge" :class="'sb-' + getSlotStatus(selectedDay, i)">
                  {{ statusLabel(getSlotStatus(selectedDay, i)) }}
                </span>

                <!-- Confirmed / Pending breakdown -->
                <span class="slot-counts">
                  <span v-if="getConfirmedCount(selectedDay, i) > 0" class="count-paid">
                    {{ getConfirmedCount(selectedDay, i) }} paid
                  </span>
                  <span v-if="getPendingCount(selectedDay, i) > 0" class="count-pending">
                    {{ getPendingCount(selectedDay, i) }} pending
                  </span>
                </span>

                <button
                  v-if="getSlotStatus(selectedDay, i) !== 'busy'"
                  type="button"
                  class="pick-btn"
                  :class="{ picked: selectedSlot && selectedSlot.start === slot.start }"
                  @click.stop="pickSlot(slot, i)"
                >
                  {{ selectedSlot && selectedSlot.start === slot.start ? '✓ Selected' : 'Select' }}
                </button>
              </div>
            </div>

          </div>
        </div>
      </transition>

    </div><!-- /cal-card -->
  </div>
</template>


<script>
import axios from 'axios';

/*
 * ── Slot definitions per service type ──────────────────────────────────────
 */
const SLOTS_BY_TYPE = {
  gully: [
    { label: '8:00 AM – 10:00 AM',  start: '08:00', end: '10:00' },
    { label: '10:30 AM – 12:30 PM', start: '10:30', end: '12:30' },
    { label: '1:30 PM – 3:30 PM',   start: '13:30', end: '15:30' },
    { label: '4:00 PM – 6:00 PM',   start: '16:00', end: '18:00' },
  ],
  bacho: [
    { label: '7:00 AM – 10:00 AM',  start: '07:00', end: '10:00' },
    { label: '10:00 AM – 1:00 PM',  start: '10:00', end: '13:00' },
    { label: '1:00 PM – 4:00 PM',   start: '13:00', end: '16:00' },
  ],
  stadiums: [
    { label: '6:00 AM – 9:00 AM',   start: '06:00', end: '09:00' },
    { label: '9:00 AM – 12:00 PM',  start: '09:00', end: '12:00' },
    { label: '12:00 PM – 3:00 PM',  start: '12:00', end: '15:00' },
    { label: '3:00 PM – 6:00 PM',   start: '15:00', end: '18:00' },
    { label: '6:00 PM – 9:00 PM',   start: '18:00', end: '21:00' },
  ],
  townhall: [
    { label: '9:00 AM – 12:00 PM',  start: '09:00', end: '12:00' },
    { label: '1:00 PM – 5:00 PM',   start: '13:00', end: '17:00' },
    { label: '6:00 PM – 10:00 PM',  start: '18:00', end: '22:00' },
  ],
  crematoria: [
    { label: '8:00 AM – 9:00 AM',   start: '08:00', end: '09:00' },
    { label: '9:00 AM – 10:00 AM',  start: '09:00', end: '10:00' },
    { label: '10:00 AM – 11:00 AM', start: '10:00', end: '11:00' },
    { label: '11:00 AM – 12:00 PM', start: '11:00', end: '12:00' },
    { label: '2:00 PM – 3:00 PM',   start: '14:00', end: '15:00' },
    { label: '3:00 PM – 4:00 PM',   start: '15:00', end: '16:00' },
  ],
};

/*
 * ── Full-day slot definitions ────────────────────────────────────────────────
 */
const FULLDAY_BY_TYPE = {
  stadiums: { label: '6:00 AM – 9:00 PM', start: '06:00', end: '21:00' },
  townhall: { label: '9:00 AM – 10:00 PM', start: '09:00', end: '22:00' },
};

/*
 * ── Capacity per service type ────────────────────────────────────────────────
 */
const CAPACITY = {
  gully: 2, bacho: 2, stadiums: 1, townhall: 1, crematoria: 1,
};

/*
 * ── Status sets ──────────────────────────────────────────────────────────────
 * ✅ 'paid' = confirmed/fully booked
 * ✅ 'waiting', 'processing', 'payment' = pending/partially booked
 */
const CONFIRMED_STATUSES = new Set(['paid']);
const PENDING_STATUSES   = new Set(['waiting', 'processing', 'payment']);

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

export default {
  name: 'AvailabilityCalendar',
  props: {
    sabhaCode:   { type: String, required: true },
    serviceType: { type: String, required: true },
    serviceId: { type: String, required: true },
  },
  emits: ['slot-selected'],

  data() {
    const now = new Date();
    return {
      curYear:    now.getFullYear(),
      curMonth:   now.getMonth(),
      today:      new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      loading:    false,
      bookedData: {},
      selectedDay:       null,
      selectedSlot:      null,
      selectedSlotLabel: '',
      isFullDaySelected: false,
      bookingMode:       'slot',
    };
  },

  computed: {
    monthLabel() {
      return `${MONTHS[this.curMonth]} ${this.curYear}`;
    },
    daysInMonth() {
      return new Date(this.curYear, this.curMonth + 1, 0).getDate();
    },
    firstOffset() {
      return new Date(this.curYear, this.curMonth, 1).getDay();
    },
    slotsForView() {
      return SLOTS_BY_TYPE[this.serviceType] || [];
    },
    fullDaySlot() {
      return FULLDAY_BY_TYPE[this.serviceType] || null;
    },
    supportsFullDay() {
      return !!FULLDAY_BY_TYPE[this.serviceType];
    },
    capacity() {
      return CAPACITY[this.serviceType] || 1;
    },
    selectedDateLabel() {
      if (!this.selectedDay) return '';
      return `${MONTHS[this.curMonth]} ${this.selectedDay}, ${this.curYear}`;
    },
  },

  watch: {
    sabhaCode()   { this.fetchAvailability(); },
    serviceType() {
      this.selectedDay       = null;
      this.selectedSlot      = null;
      this.selectedSlotLabel = '';
      this.isFullDaySelected = false;
      this.bookingMode       = 'slot';
      this.fetchAvailability();
    },
  },

  mounted() {
    this.fetchAvailability();
  },

  methods: {
    /* ── Helpers ── */
    dateKey(d) {
      const m  = String(this.curMonth + 1).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      return `${this.curYear}-${m}-${dd}`;
    },
    isPast(d) {
      return new Date(this.curYear, this.curMonth, d) < this.today;
    },
    isToday(d) {
      return new Date(this.curYear, this.curMonth, d).getTime() === this.today.getTime();
    },

    /* ── Core status logic ── */
    getBookingsForSlot(d, slot) {
      const key  = this.dateKey(d);
      const rows = this.bookedData[key] || [];
      return rows.filter(b => {
        // ✅ Normalise to HH:MM for safe string comparison
        const bStart = b.start.slice(0, 5);
        const bEnd   = b.end.slice(0, 5);
        return bStart < slot.end && bEnd > slot.start;
      });
    },

    getConfirmedCount(d, slotIndex) {
      const slot = this.slotsForView[slotIndex];
      if (!slot) return 0;
      return this.getBookingsForSlot(d, slot)
        .filter(b => CONFIRMED_STATUSES.has(b.status)).length;
    },

    getPendingCount(d, slotIndex) {
      const slot = this.slotsForView[slotIndex];
      if (!slot) return 0;
      return this.getBookingsForSlot(d, slot)
        .filter(b => PENDING_STATUSES.has(b.status)).length;
    },

    /*
     * ✅ Status rules:
     *   confirmed >= capacity            → 'busy'    (fully booked — cannot select)
     *   confirmed > 0 OR pending > 0     → 'partial' (has activity — can still select with warning)
     *   otherwise                        → 'avail'   (free)
     */
    getSlotStatus(d, slotIndex) {
      const confirmed = this.getConfirmedCount(d, slotIndex);
      const pending   = this.getPendingCount(d, slotIndex);
      const cap       = this.capacity;

      if (confirmed >= cap)              return 'busy';
      if (confirmed > 0 || pending > 0)  return 'partial';
      return 'avail';
    },

    isDayFullyBooked(d) {
      return this.slotsForView.every((_, i) => this.getSlotStatus(d, i) === 'busy');
    },

    canBookFullDay(d) {
      const key = this.dateKey(d);
      return !(this.bookedData[key] && this.bookedData[key].length > 0);
    },

    statusLabel(s) {
      if (s === 'avail')   return 'Available';
      if (s === 'busy')    return 'Fully booked';
      return 'Partially booked';
    },

    /* ── Data fetching ── */
    async fetchAvailability() {
      if (!this.sabhaCode || !this.serviceType) return;
      this.loading = true;
      try {
        const month = String(this.curMonth + 1).padStart(2, '0');
        const res   = await axios.get(
          `/availability/${this.sabhaCode}/${this.serviceType}/${this.serviceId}/${this.curYear}/${month}`
        );

        const grouped = {};

        for (const row of res.data) {
          /*
           * ✅ KEY FIX: req_date_from is already a plain "YYYY-MM-DD" string
           * from DATE_FORMAT() in the backend SQL.
           * Do NOT wrap in new Date() — that would shift the date by UTC offset.
           */
          const key = row.req_date_from; // e.g. "2026-05-08" — use directly

          if (!grouped[key]) grouped[key] = [];

          const rawStatus = String(row.payment_status || '').trim().toLowerCase();

          grouped[key].push({
            start:  row.req_start_time.slice(0, 5),   // "HH:MM"
            end:    row.req_end_time.slice(0, 5),      // "HH:MM"
            status: rawStatus,
          });

          // Debug — remove after confirming
          console.log(`[booking] key=${key} start=${row.req_start_time.slice(0,5)} end=${row.req_end_time.slice(0,5)} status="${rawStatus}" isConfirmed=${CONFIRMED_STATUSES.has(rawStatus)} isPending=${PENDING_STATUSES.has(rawStatus)}`);
        }

        this.bookedData = grouped;
        console.log('bookedData:', JSON.stringify(grouped, null, 2));
      } catch (err) {
        console.error('Availability fetch error:', err);
      } finally {
        this.loading = false;
      }
    },

    /* ── Navigation ── */
    changeMonth(dir) {
      let m = this.curMonth + dir;
      let y = this.curYear;
      if (m > 11) { m = 0; y++; }
      if (m < 0)  { m = 11; y--; }
      const now = new Date();
      if (y < now.getFullYear() || (y === now.getFullYear() && m < now.getMonth())) return;
      this.curMonth          = m;
      this.curYear           = y;
      this.selectedDay       = null;
      this.selectedSlot      = null;
      this.isFullDaySelected = false;
      this.bookingMode       = 'slot';
      this.selectedSlotLabel = '';
      this.fetchAvailability();
    },

    /* ── User interactions ── */
    selectDay(d) {
      if (this.isPast(d)) return;
      this.selectedDay       = d;
      this.selectedSlot      = null;
      this.isFullDaySelected = false;
      this.selectedSlotLabel = '';
      this.bookingMode       = 'slot';
    },

    /*
     * ✅ pickSlot now passes `status` in the emit payload
     * so OnlineBooking.vue can decide whether to warn or block.
     */
    pickSlot(slot, slotIndex) {
      const status = this.getSlotStatus(this.selectedDay, slotIndex);

      // Extra safety: never allow picking a busy slot
      if (status === 'busy') return;

      this.selectedSlot      = slot;
      this.isFullDaySelected = false;
      const dateStr          = this.dateKey(this.selectedDay);
      this.selectedSlotLabel = `${this.selectedDateLabel} | ${slot.label}`;

      this.$emit('slot-selected', {
        date:      dateStr,
        start:     slot.start,
        end:       slot.end,
        isFullDay: false,
        label:     this.selectedSlotLabel,
        status,               // ✅ 'avail' | 'partial' — parent uses this to warn/allow
      });
    },

    pickFullDay() {
      const fd = this.fullDaySlot;
      if (!fd) return;
      this.isFullDaySelected = true;
      this.selectedSlot      = fd;
      const dateStr          = this.dateKey(this.selectedDay);
      this.selectedSlotLabel = `Full day — ${this.selectedDateLabel} | ${fd.label}`;

      this.$emit('slot-selected', {
        date:      dateStr,
        start:     fd.start,
        end:       fd.end,
        isFullDay: true,
        label:     this.selectedSlotLabel,
        status:    'avail',   // full day only selectable when completely free
      });
    },

    /* ✅ Emits null so parent's onSlotSelected resets correctly */
    clearSlot() {
      this.selectedSlot      = null;
      this.isFullDaySelected = false;
      this.selectedSlotLabel = '';
      this.$emit('slot-selected', null);
    },
  },
};
</script>


<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
.avail-calendar { font-family: 'DM Sans', sans-serif; }

/* Slot banner */
.fade-enter-active { transition: all 0.25s ease; }
.fade-enter-from   { opacity: 0; transform: translateY(6px); }
.slot-banner {
  display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap;
  padding: 0.85rem 1.2rem; margin-bottom: 1.2rem;
  background: #EAF3DE; border: 1.5px solid #97C459; border-radius: 0.8rem;
  font-size: 1.2rem; color: #3B6D11;
}
.slot-banner svg { width: 1.4rem; height: 1.4rem; stroke: #3B6D11; flex-shrink: 0; }
.slot-banner strong { color: #27500A; }
.change-btn {
  margin-left: auto; padding: 0.3rem 0.9rem; border-radius: 0.5rem;
  border: 1.5px solid #639922; background: #fff; color: #3B6D11;
  font-family: 'DM Sans', sans-serif; font-size: 1.1rem; font-weight: 600; cursor: pointer;
}
.change-btn:hover { background: #639922; color: #fff; }

/* Calendar card */
.cal-card {
  border: 1.5px solid #e5ddd5; border-radius: 1rem;
  overflow: hidden; background: #fff;
}

/* Month nav */
.cal-nav {
  display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap;
  padding: 1rem 1.4rem; border-bottom: 1px solid #f0e6de; background: #faf8f6;
}
.nav-btn {
  width: 2.4rem; height: 2.4rem; border-radius: 0.5rem;
  border: 1.5px solid #e5ddd5; background: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: #4a3028; transition: all 0.15s;
}
.nav-btn svg { width: 1.2rem; height: 1.2rem; }
.nav-btn:hover { background: #7c2d12; border-color: #7c2d12; color: #fff; }
.month-label { font-size: 1.3rem; font-weight: 700; color: #1c0f0a; min-width: 160px; text-align: center; }
.cal-legend  { display: flex; gap: 1rem; flex-wrap: wrap; margin-left: auto; }
.leg         { display: flex; align-items: center; gap: 0.4rem; font-size: 1.1rem; color: #7a6258; }
.leg-dot     { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.leg-avail   { background: #5caa1b; }
.leg-partial { background: #f5a534; }
.leg-full    { background: #b70c0c; }

/* Status key bar */
.status-key {
  display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
  padding: 0.7rem 1.4rem; border-bottom: 1px solid #f0e6de;
  background: #fdf9f6; font-size: 1.05rem;
}
.sk-item  { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.sk-arrow { font-size: 1.1rem; color: #9a8070; }
.sk-label { font-size: 1.05rem; font-weight: 600; color: #4a3028; }
.sk-sep   { color: #d0c5bc; font-size: 1.3rem; }
.status-pill {
  font-size: 1rem; font-weight: 600; padding: 0.2rem 0.75rem;
  border-radius: 99px; white-space: nowrap;
}
.sp-waiting    { background: #dbeafe; color: #1d4ed8; }
.sp-processing { background: #fef9c3; color: #92400e; }
.sp-payment    { background: #ccfbf1; color: #0f766e; }
.sp-paid       { background: #dcfce7; color: #166534; }

/* Loading */
.cal-loading {
  display: flex; align-items: center; justify-content: center;
  gap: 0.8rem; padding: 3rem; font-size: 1.2rem; color: #9a8070;
}
.spin { width: 1.6rem; height: 1.6rem; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Grid */
.cal-grid {
  display: grid; grid-template-columns: repeat(7, 1fr);
  gap: 3px; padding: 1rem;
}
.cal-dh {
  font-size: 1rem; font-weight: 600; color: #9a8070;
  text-align: center; padding: 0.3rem 0; text-transform: uppercase; letter-spacing: 0.05em;
}
.cal-empty { min-height: 60px; }
.cal-day {
  border: 1.5px solid #e5ddd5; border-radius: 0.6rem;
  padding: 0.45rem 0.35rem; min-height: 60px;
  cursor: pointer; background: #fff; transition: all 0.15s;
}
.cal-day:hover:not(.cal-past):not(.cal-fullday) { border-color: #c2440a; background: #fff8f5; }
.cal-past    { opacity: 0.35; cursor: not-allowed; }
.cal-fullday { background: #fff0f0; border-color: #fca5a5; cursor: not-allowed; }
.cal-today .cal-day-num {
  background: #7c2d12; color: #fff; border-radius: 50%;
  width: 22px; height: 22px; display: flex; align-items: center; justify-content: center;
}
.cal-selected { border: 2px solid #c2440a !important; background: #fff8f5 !important; }
.cal-day-num  { font-size: 1.1rem; font-weight: 600; color: #1c0f0a; margin-bottom: 3px; display: block; }
.fullday-bar  {
  font-size: 0.85rem; font-weight: 700; color: #791F1F;
  background: #F7C1C1; border-radius: 3px; padding: 1px 4px;
  text-align: center; letter-spacing: 0.03em;
}
.cal-dots { display: flex; flex-wrap: wrap; gap: 2px; }
.cal-dot  { width: 6px; height: 6px; border-radius: 50%; }
.dot-avail   { background: #5caa1b; }
.dot-partial { background: #f5a534; }
.dot-busy    { background: #b70c0c; }

/* Slot panel */
.slot-panel { border-top: 1px solid #f0e6de; }
.slot-panel-hd {
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.9rem 1.4rem; background: #faf8f6; border-bottom: 1px solid #f0e6de;
  font-size: 1.2rem; color: #4a3028;
}
.slot-panel-hd svg { width: 1.4rem; height: 1.4rem; stroke: #c2440a; flex-shrink: 0; }
.slot-panel-hd strong { color: #1c0f0a; }

/* Booking type tabs */
.type-tabs {
  display: flex; gap: 0.6rem; padding: 0.9rem 1.4rem;
  border-bottom: 1px solid #f0e6de; background: #faf8f6;
}
.type-tab {
  display: flex; align-items: center; gap: 0.5rem; flex: 1;
  padding: 0.7rem 1rem; border: 1.5px solid #e5ddd5;
  border-radius: 0.7rem; background: #fff; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 1.15rem; font-weight: 600;
  color: #7a6258; transition: all 0.15s;
}
.type-tab svg { width: 1.3rem; height: 1.3rem; stroke: #9a8070; flex-shrink: 0; }
.type-tab:hover { border-color: #c2440a; color: #7c2d12; }
.type-tab:hover svg { stroke: #c2440a; }
.type-tab.active { border-color: #c2440a; background: #fff8f5; color: #7c2d12; }
.type-tab.active svg { stroke: #c2440a; }
.tab-sub { font-size: 1rem; color: #9a8070; font-weight: 400; margin-left: auto; }
.type-tab.active .tab-sub { color: #9a6652; }

/* Full day section */
.fullday-section { padding: 1rem 1.4rem; }
.fd-available {
  display: flex; align-items: center; gap: 0.9rem; flex-wrap: wrap;
  padding: 1rem 1.2rem; border: 1.5px solid #97C459; border-radius: 0.8rem;
  background: #EAF3DE; font-size: 1.2rem; color: #3B6D11;
}
.fd-available svg { width: 1.4rem; height: 1.4rem; stroke: #3B6D11; flex-shrink: 0; }
.fd-available strong { color: #27500A; display: block; margin-bottom: 0.1rem; }
.fd-sub { font-size: 1.05rem; color: #3B6D11; }
.fd-unavail {
  display: flex; align-items: flex-start; gap: 0.8rem;
  padding: 1rem 1.2rem; border: 1px solid #fca5a5; border-radius: 0.8rem;
  background: #fff0f0; font-size: 1.15rem; color: #991b1b; line-height: 1.5;
  margin: 1rem 1.4rem;
}
.fd-unavail svg { width: 1.4rem; height: 1.4rem; stroke: #A32D2D; flex-shrink: 0; margin-top: 0.1rem; }

/* Slot list */
.slot-list { padding: 1rem 1.4rem; display: flex; flex-direction: column; gap: 0.5rem; }
.slot-row {
  display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap;
  padding: 0.8rem 1rem; border: 1.5px solid #e5ddd5; border-radius: 0.7rem;
  background: #faf8f6; cursor: pointer; transition: all 0.15s;
}
.slot-row:not(.slot-busy):hover { border-color: #c2440a; background: #fff8f5; }
.slot-picked { border-color: #c2440a !important; background: #fff8f5 !important; }
.slot-busy   { opacity: 0.55; cursor: not-allowed; }
.slot-row svg { width: 1.3rem; height: 1.3rem; stroke: #c2440a; flex-shrink: 0; }
.slot-busy svg { stroke: #9a8070; }
.slot-time  { font-size: 1.2rem; font-weight: 600; color: #1c0f0a; min-width: 155px; }
.slot-badge {
  font-size: 1.05rem; font-weight: 600; padding: 0.2rem 0.8rem;
  border-radius: 99px; white-space: nowrap;
}
.sb-avail   { background: #EAF3DE; color: #27500A; }
.sb-partial { background: #FAEEDA; color: #854F0B; }
.sb-busy    { background: #FCEBEB; color: #A32D2D; }

/* Count pills */
.slot-counts { display: flex; gap: 0.4rem; align-items: center; }
.count-paid {
  font-size: 1rem; padding: 0.15rem 0.6rem; border-radius: 99px;
  background: #dcfce7; color: #166534; font-weight: 600;
}
.count-pending {
  font-size: 1rem; padding: 0.15rem 0.6rem; border-radius: 99px;
  background: #fef9c3; color: #92400e; font-weight: 600;
}

/* Pick button */
.pick-btn {
  margin-left: auto; padding: 0.45rem 1.1rem; border-radius: 0.6rem;
  border: 1.5px solid #c2440a; color: #c2440a; background: #fff;
  font-family: 'DM Sans', sans-serif; font-size: 1.15rem; font-weight: 600;
  cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.pick-btn:hover, .pick-btn.picked { background: #c2440a; color: #fff; }

/* Responsive */
@media (max-width: 600px) {
  .cal-nav    { flex-direction: column; align-items: flex-start; }
  .cal-legend { margin-left: 0; }
  .status-key { flex-direction: column; gap: 0.6rem; }
  .slot-counts { display: none; }
  .cal-grid   { gap: 2px; padding: 0.6rem; }
  .cal-day    { min-height: 50px; }
}
</style>